import {
  PartnerAudit,
  PartnerDetail,
  PartnerStatsRow,
  type PartnerPeriod,
  Action,
  ChatWebhookStatus,
  ChatWebhookTestResult,
  CoachToday,
  ClientOptionList,
  type AddChecklistItemBody,
  type UpdateChecklistItemBody,
  SkipR2Result,
  ClearNoSubscriptionResult,
  ClearQuoteFollowupOwnerResult,
  ClientNoSubscriptionList,
  ClientQuoteFollowupOwnerList,
  NoSubscriptionResult,
  type NoSubscriptionBody,
  QuoteDoneResult,
  type QuoteDoneBody,
  R1ClientResult,
  type R1ClientBody,
  ActionStakes,
  DraftEmail,
  type ActionsQuery,
  type CreateActionBody,
  type DraftEmailBody,
  type UpdateActionBody,
  TaskNotifyOptions,
  TaskNotifyResult,
  type TaskNotifyBody,
  GeocodingOverview,
  GeocodingRunQueued,
  MapClientsResponse,
  AcquisitionMetrics,
  AcquisitionChannelOption,
  AxonautSortingRow,
  UnknownApporteursList,
  CashflowMetrics,
  Expense,
  ExpensesMetrics,
  FigureDetail,
  type FigureDetailQuery,
  ImportResult,
  OverviewMetrics,
  Partner,
  Quote,
  RevenueDetailsMetrics,
  RevenueMetrics,
  SubscriptionsMetrics,
  ManualSubscription,
  ManualSubscriptionsAdmin,
  type CreateManualSubscriptionBody,
  type UpdateManualSubscriptionBody,
  ObjectivesYear,
  IgnoredSite,
  type IgnoreSiteBody,
  UnlinkedItem,
  UnlinkedList,
  UnlinkedSiteItem,
  type CreatePartnerBody,
  type LinkUnlinkedBody,
  type UnlinkedKind,
  type BoardColumn,
  type UpdateObjectivesBody,
  UpdateExpenseResult,
  AccountCodes,
  ApplyGroupResult,
  ExpenseAiRunResponse,
  ExpenseLineDetail,
  ReviewGroups,
  UncategorizedGroups,
  ValidateReviewResult,
  type ApplyGroupBody,
  CreateExpenseRuleResult,
  ExpenseCategoryVat,
  ExpenseCategoryList,
  ExpenseCategorySummary,
  type CreateExpenseCategoryBody,
  type UpdateExpenseCategoryBody,
  type SplitExpenseCategoryBody,
  SplitExpenseCategoryResult,
  ExpenseEstimates,
  ExpenseRule,
  RecomputeEstimatesResult,
  type CreateExpenseRuleBody,
  type ExpensesQuery,
  type UpdateExpenseBody,
  type UpdatePartnerBody,
  type UpdateQuoteBody,
  AgendaUpcoming,
  AgendaWeek,
  R1Brief,
  R1Detail,
  R1PrepResult,
  QuoteCatalog,
  QuoteClientSearchResult,
  QuoteDraft,
  QuoteDraftSummaryList,
  type OpenQuoteDraftBody,
  type UpdateQuoteDraftBody,
  type R1BriefRequest,
  type R1PrepInput,
  ProjectDetail,
  ProjectBoard,
  ProjectBoardChange,
  ProjectBoardItem,
  ProjectList,
  ProjectOrderState,
  ProjectLoad,
  ProjectStep,
  SettingResponse,
  SimulateProjectResult,
  type SettingKey,
  type SimulateProjectBody,
  Feedback,
  FeedbackList,
  MarkNotificationsReadResult,
  NotificationList,
  Preferences,
  Changelog,
  PushConfig,
  PushTestResult,
  QuoteSignedEvent,
  AdminUserList,
  AuthConfig,
  Connection,
  ConnectionSyncQueued,
  ConnectionTestResult,
  GoogleAccountStatus,
  GoogleCalendarMemberList,
  GoogleChatSpaceList,
  GmailHistoryStatus,
  MergeUserResult,
  SharedMailboxSettings,
  SharedMailboxTestResult,
  type UpdateSharedMailboxBody,
  Me,
  Plan,
  PlanRun,
  SetupStatus,
  ConnectionsOverview,
  MemberList,
  User,
  type CreateFeedbackBody,
  type CreatePlanBody,
  type PreferencesPatch,
  type PushSubscriptionBody,
  type SimulateSignatureBody,
  type UpdateFeedbackBody,
  type DevLoginBody,
  type ExecutePlanBody,
  type InviteUserBody,
  type ProviderName,
  type UpdateConnectionBody,
  type UpdateUserBody,
  MeetingProposalList,
  PendingMoveList,
} from '@cockpit/shared';
import { request, requestText, uploadFile, uploadForm } from './client';
import { z } from 'zod';

/** Imports des Google Sheets (CDC §3.7) : un lien de Sheet ou un fichier .xlsx. */
export type ImportKind = 'sheet-finance' | 'sheet-timeline';
export type ImportSource = { spreadsheetUrl: string } | { file: File };

function importPath(kind: ImportKind, options: { dryRun: boolean; overwriteObjectives?: boolean }): string {
  const params = new URLSearchParams();
  if (options.dryRun) params.set('dryRun', '1');
  if (options.overwriteObjectives) params.set('overwriteObjectives', '1');
  const query = params.toString();
  return `/api/admin/import/${kind}${query ? `?${query}` : ''}`;
}

export { ApiError } from './client';

/** Client typé de l'API : chaque réponse est validée par le schéma partagé correspondant. */
export const api = {
  authConfig: () => request('GET', '/api/auth/config', { schema: AuthConfig }),
  me: () => request('GET', '/api/me', { schema: Me }),
  devLogin: (body: DevLoginBody) => request('POST', '/api/auth/dev-login', { body, schema: Me }),
  logout: () => request('POST', '/api/auth/logout'),
  googleStartUrl: '/api/auth/google/start',

  listUsers: () => request('GET', '/api/admin/users', { schema: AdminUserList }),
  inviteUser: (body: InviteUserBody) => request('POST', '/api/admin/users', { body, schema: User }),
  updateUser: (id: string, body: UpdateUserBody) =>
    request('PATCH', `/api/admin/users/${encodeURIComponent(id)}`, { body, schema: User }),
  /** Lot S : fond le membre `id` (doublon, supprimé) dans `intoUserId`. */
  mergeUser: (id: string, intoUserId: string) =>
    request('POST', `/api/admin/users/${encodeURIComponent(id)}/merge`, {
      body: { intoUserId },
      schema: MergeUserResult,
    }),
  /** Lot S : profondeur et avancement de la reprise de l'historique Gmail. */
  gmailHistory: () => request('GET', '/api/admin/google/gmail-history', { schema: GmailHistoryStatus }),
  setGmailHistory: (months: number) =>
    request('PUT', '/api/admin/google/gmail-history', { body: { months }, schema: GmailHistoryStatus }),

  getConnection: (provider: ProviderName) =>
    request('GET', `/api/admin/connections/${provider}`, { schema: Connection }),
  updateConnection: (provider: ProviderName, body: UpdateConnectionBody) =>
    request('PUT', `/api/admin/connections/${provider}`, { body, schema: Connection }),
  testConnection: (provider: ProviderName) =>
    request('POST', `/api/admin/connections/${provider}/test`, { schema: ConnectionTestResult }),
  syncConnection: (provider: ProviderName) =>
    request('POST', `/api/admin/connections/${provider}/sync`, { schema: ConnectionSyncQueued }),

  setupStatus: () => request('GET', '/api/setup/status', { schema: SetupStatus }),
  members: () => request('GET', '/api/members', { schema: MemberList }),
  connectionsOverview: () => request('GET', '/api/connections/overview', { schema: ConnectionsOverview }),

  /** « Mon compte Google » : état de l'autorisation Google de la personne connectée. */
  googleAccount: () => request('GET', '/api/me/google', { schema: GoogleAccountStatus }),
  disconnectGoogle: () => request('DELETE', '/api/me/google', { schema: GoogleAccountStatus }),
  /** Navigation (pas un appel fetch) : redirige vers l'écran d'autorisation de Google. */
  googleConnectUrl: '/api/me/google/connect',
  googleChatSpaces: () => request('GET', '/api/admin/google/chat-spaces', { schema: GoogleChatSpaceList }),
  /** Lot H : agenda personnel de chaque membre (pas d'agenda partagé). */
  googleCalendarMembers: () =>
    request('GET', '/api/admin/google/calendar-members', { schema: GoogleCalendarMemberList }),
  /** Lot L : boîte partagée (compte de service ; la clé n'est jamais renvoyée). */
  sharedMailboxes: () =>
    request('GET', '/api/admin/google/shared-mailboxes', { schema: SharedMailboxSettings }),
  updateSharedMailboxes: (body: UpdateSharedMailboxBody) =>
    request('PUT', '/api/admin/google/shared-mailboxes', { body, schema: SharedMailboxSettings }),
  testSharedMailboxes: () =>
    request('POST', '/api/admin/google/shared-mailboxes/test', { schema: SharedMailboxTestResult }),

  createPlan: (body: CreatePlanBody) => request('POST', '/api/plans', { body, schema: Plan }),
  executePlan: (id: string, body: ExecutePlanBody) =>
    request('POST', `/api/plans/${encodeURIComponent(id)}/execute`, { body, schema: PlanRun }),
  cancelPlan: (id: string) =>
    request('POST', `/api/plans/${encodeURIComponent(id)}/cancel`, { schema: Plan }),
  /** Lot BI : « Annuler » pendant le délai (409 `PLAN_ALREADY_STARTED` si l'exécution est partie). */
  // Lot BQ : déplacements validés, pas encore exécutés (relus après un rechargement de la page).
  pendingMoves: () => request('GET', '/api/plans/pending-moves', { schema: PendingMoveList }),
  undoPlan: (id: string) => request('POST', `/api/plans/${encodeURIComponent(id)}/undo`, { schema: Plan }),
  /** Lot BI : le plan, avec ses résultats et ses conséquences une fois exécuté. */
  getPlan: (id: string) => request('GET', `/api/plans/${encodeURIComponent(id)}`, { schema: PlanRun }),

  updatePreferences: (body: PreferencesPatch) =>
    request('PATCH', '/api/me/preferences', { body, schema: Preferences }),
  /** Lot BF : coach de journée (bandeau, « Ta journée », « Rappels du jour »). */
  coachToday: () => request('GET', '/api/coach/today', { schema: CoachToday }),
  /** Lot BF : webhook Google Chat du membre (jamais renvoyé : seulement « configuré »). */
  putChatWebhook: (url: string | null) =>
    request('PUT', '/api/me/chat-webhook', { body: { url }, schema: ChatWebhookStatus }),
  testChatWebhook: () => request('POST', '/api/me/chat-webhook/test', { schema: ChatWebhookTestResult }),
  /** Lot BA : journal des versions (« Quoi de neuf », page Nouveautés). */
  changelog: () => request('GET', '/api/changelog', { schema: Changelog }),

  listFeedback: (status: 'open' | 'all' = 'all') =>
    request('GET', `/api/feedback?status=${status}`, { schema: FeedbackList }),
  createFeedback: (body: CreateFeedbackBody) => request('POST', '/api/feedback', { body, schema: Feedback }),
  updateFeedback: (id: string, body: UpdateFeedbackBody) =>
    request('PATCH', `/api/feedback/${encodeURIComponent(id)}`, { body, schema: Feedback }),
  uploadFeedbackAttachment: (id: string, file: Blob) =>
    uploadFile(`/api/feedback/${encodeURIComponent(id)}/attachments`, file, Feedback),
  exportFeedback: () => requestText('/api/feedback/export'),

  notifications: () => request('GET', '/api/notifications', { schema: NotificationList }),
  markNotificationsRead: (ids?: string[]) =>
    request('POST', '/api/notifications/read', {
      body: ids ? { ids } : {},
      schema: MarkNotificationsReadResult,
    }),
  pushConfig: () => request('GET', '/api/push/config', { schema: PushConfig }),
  pushSubscribe: (body: PushSubscriptionBody) => request('POST', '/api/push/subscribe', { body }),
  pushUnsubscribe: (endpoint: string) => request('DELETE', '/api/push/subscribe', { body: { endpoint } }),
  pushTest: () => request('POST', '/api/push/test', { schema: PushTestResult }),
  // ---------------------------------------------------------------- Lot B : chiffres et données
  metricsOverview: () => request('GET', '/api/metrics/overview', { schema: OverviewMetrics }),
  metricsRevenue: (year: number) =>
    request('GET', `/api/metrics/revenue?year=${year}`, { schema: RevenueMetrics }),
  metricsExpenses: (year: number) =>
    request('GET', `/api/metrics/expenses?year=${year}`, { schema: ExpensesMetrics }),
  metricsCashflow: (weeks = 13) =>
    request('GET', `/api/metrics/cashflow?weeks=${weeks}`, { schema: CashflowMetrics }),
  metricsSubscriptions: () => request('GET', '/api/metrics/subscriptions', { schema: SubscriptionsMetrics }),
  metricsAcquisition: () => request('GET', '/api/metrics/acquisition', { schema: AcquisitionMetrics }),
  /** Lot V : onglet Chiffres selon l'interrupteur Signé / Facturé. */
  /** Lot AG [13] : lignes qui composent un chiffre (fenêtre de détail). */
  metricsDetail: (query: FigureDetailQuery) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
    return request('GET', `/api/metrics/detail?${params.toString()}`, { schema: FigureDetail });
  },
  metricsRevenueDetails: (year: number) =>
    request('GET', `/api/metrics/revenue-details?year=${year}`, { schema: RevenueDetailsMetrics }),

  uncategorizedExpenses: () => request('GET', '/api/expenses?uncategorized=1', { schema: z.array(Expense) }),
  categorizeExpense: (id: string, categoryId: string | null) =>
    request('PATCH', `/api/expenses/${encodeURIComponent(id)}`, {
      body: { categoryId },
      schema: UpdateExpenseResult,
    }),
  // Lot P : détail, « Pas une dépense », doublons, règles, IA, estimation de fin d'année.
  expenses: (query: ExpensesQuery) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
    return request('GET', `/api/expenses?${params.toString()}`, { schema: z.array(Expense) });
  },
  updateExpense: (id: string, body: UpdateExpenseBody) =>
    request('PATCH', `/api/expenses/${encodeURIComponent(id)}`, { body, schema: UpdateExpenseResult }),
  expenseRules: () => request('GET', '/api/expense-rules', { schema: z.array(ExpenseRule) }),
  createExpenseRule: (body: CreateExpenseRuleBody) =>
    request('POST', '/api/expense-rules', { body, schema: CreateExpenseRuleResult }),
  deleteExpenseRule: (id: string) => request('DELETE', `/api/expense-rules/${encodeURIComponent(id)}`),
  // Lot Y [14] : l'IA sur tout ce qui reste (passage en file, progression lue par le GET).
  categorizeExpensesWithAi: () =>
    request('POST', '/api/expenses/categorize-ai', { schema: ExpenseAiRunResponse }),
  expenseAiRun: () => request('GET', '/api/expenses/categorize-ai', { schema: ExpenseAiRunResponse }),
  // Lot Y [13] [15] [16] [17].
  expensesToCategorize: () => request('GET', '/api/expenses/to-categorize', { schema: UncategorizedGroups }),
  applyExpenseGroup: (body: ApplyGroupBody) =>
    request('POST', '/api/expenses/to-categorize/apply', { body, schema: ApplyGroupResult }),
  expenseAccountCodes: (year?: number) =>
    request('GET', `/api/expenses/account-codes${year ? `?year=${year}` : ''}`, { schema: AccountCodes }),
  setExpenseAccountCode: (code: string, categoryId: string) =>
    request('PUT', '/api/expenses/account-codes', { body: { code, categoryId }, schema: AccountCodes }),
  removeExpenseAccountCode: (code: string) =>
    request('DELETE', `/api/expenses/account-codes/${encodeURIComponent(code)}`, { schema: AccountCodes }),
  expenseReview: () => request('GET', '/api/expenses/review', { schema: ReviewGroups }),
  setExpenseReviewThreshold: (reviewMinCents: number) =>
    request('PUT', '/api/expenses/review/threshold', { body: { reviewMinCents }, schema: ReviewGroups }),
  validateExpenseReview: (expenseIds: string[]) =>
    request('POST', '/api/expenses/review/validate', { body: { expenseIds }, schema: ValidateReviewResult }),
  expenseLineDetail: (id: string) =>
    request('GET', `/api/expenses/${encodeURIComponent(id)}/detail`, { schema: ExpenseLineDetail }),
  setCategoryVat: (id: string, vatRecoverable: boolean) =>
    request('PATCH', `/api/expense-categories/${encodeURIComponent(id)}`, {
      body: { vatRecoverable },
      schema: ExpenseCategoryVat,
    }),
  /** Lot AH [21] : postes de dépenses dans Réglages > Dépenses (administrateur). */
  expenseCategories: () => request('GET', '/api/expense-categories', { schema: ExpenseCategoryList }),
  createExpenseCategory: (body: CreateExpenseCategoryBody) =>
    request('POST', '/api/expense-categories', { body, schema: ExpenseCategorySummary }),
  updateExpenseCategory: (id: string, body: UpdateExpenseCategoryBody) =>
    request('PATCH', `/api/expense-categories/${encodeURIComponent(id)}`, {
      body,
      schema: ExpenseCategorySummary,
    }),
  splitExpenseCategory: (id: string, body: SplitExpenseCategoryBody) =>
    request('POST', `/api/expense-categories/${encodeURIComponent(id)}/split`, {
      body,
      schema: SplitExpenseCategoryResult,
    }),
  expenseEstimates: () => request('GET', '/api/expenses/estimates', { schema: ExpenseEstimates }),
  setExpenseEstimate: (categoryId: string, month: string, manualCents: number | null) =>
    request('PUT', `/api/expenses/estimates/${encodeURIComponent(categoryId)}/${month}`, {
      body: { manualCents },
      schema: ExpenseEstimates,
    }),
  recomputeExpenseEstimates: () =>
    request('POST', '/api/admin/expenses/estimates/recompute', { schema: RecomputeEstimatesResult }),

  unattributedQuotes: () => request('GET', '/api/quotes?attributed=0', { schema: z.array(Quote) }),
  attributeQuote: (id: string, body: UpdateQuoteBody) =>
    request('PATCH', `/api/quotes/${encodeURIComponent(id)}`, { body, schema: Quote }),
  partners: () => request('GET', '/api/partners', { schema: z.array(Partner) }),
  createPartner: (body: CreatePartnerBody) => request('POST', '/api/partners', { body, schema: Partner }),
  updatePartner: (id: string, body: UpdatePartnerBody) =>
    request('PATCH', `/api/partners/${encodeURIComponent(id)}`, { body, schema: Partner }),
  // Lot W : autres noms d'un apporteur, canaux dynamiques, tri des fiches Axonaut.
  addPartnerAlias: (id: string, name: string) =>
    request('POST', `/api/partners/${encodeURIComponent(id)}/aliases`, { body: { name }, schema: Partner }),
  removePartnerAlias: (id: string, aliasId: string) =>
    request('DELETE', `/api/partners/${encodeURIComponent(id)}/aliases/${encodeURIComponent(aliasId)}`, {
      schema: Partner,
    }),
  // Lot BN : tableau par apporteur, détail, carte « À vérifier ».
  partnerStats: (period: PartnerPeriod) =>
    request('GET', `/api/partners/stats?period=${period}`, { schema: z.array(PartnerStatsRow) }),
  partnerDetail: (id: string, period: PartnerPeriod) =>
    request('GET', `/api/partners/${encodeURIComponent(id)}/detail?period=${period}`, {
      schema: PartnerDetail,
    }),
  partnerAudit: (period: PartnerPeriod) =>
    request('GET', `/api/partners/audit?period=${period}`, { schema: PartnerAudit }),
  dismissPartnerAudit: (key: string) =>
    request('POST', `/api/partners/audit/${encodeURIComponent(key)}/dismiss`),
  undismissPartnerAudit: (key: string) =>
    request('DELETE', `/api/partners/audit/${encodeURIComponent(key)}/dismiss`),
  acquisitionChannels: () =>
    request('GET', '/api/acquisition/channels', { schema: z.array(AcquisitionChannelOption) }),
  unknownApporteurs: () =>
    request('GET', '/api/acquisition/unknown-apporteurs', { schema: UnknownApporteursList }),
  ignoreApporteur: (name: string) =>
    request('POST', '/api/acquisition/unknown-apporteurs/ignore', {
      body: { name },
      schema: UnknownApporteursList,
    }),
  unignoreApporteur: (name: string) =>
    request('POST', '/api/acquisition/unknown-apporteurs/unignore', {
      body: { name },
      schema: UnknownApporteursList,
    }),
  axonautSorting: () => request('GET', '/api/acquisition/clients', { schema: z.array(AxonautSortingRow) }),

  unlinked: () => request('GET', '/api/admin/unlinked', { schema: UnlinkedList }),
  linkUnlinked: (kind: UnlinkedKind, id: string, body: LinkUnlinkedBody) =>
    request('PATCH', `/api/admin/unlinked/${kind}/${encodeURIComponent(id)}`, {
      body,
      schema: UnlinkedItem,
    }),
  /** Lot R : ignorer un site du back qui n'est pas un client (local au cockpit), le rétablir. */
  ignoreSite: (id: string, body: IgnoreSiteBody) =>
    request('POST', `/api/admin/unlinked/site/${encodeURIComponent(id)}/ignore`, {
      body,
      schema: IgnoredSite,
    }),
  restoreSite: (id: string) =>
    request('DELETE', `/api/admin/unlinked/site/${encodeURIComponent(id)}/ignore`, {
      schema: UnlinkedSiteItem,
    }),
  updateObjectives: (year: number, body: UpdateObjectivesBody) =>
    request('PUT', `/api/admin/objectives/${year}`, { body, schema: ObjectivesYear }),

  /** Lot J : abonnements facturés hors Stripe (administrateur). */
  manualSubscriptions: () =>
    request('GET', '/api/admin/manual-subscriptions', { schema: ManualSubscriptionsAdmin }),
  createManualSubscription: (body: CreateManualSubscriptionBody) =>
    request('POST', '/api/admin/manual-subscriptions', { body, schema: ManualSubscription }),
  updateManualSubscription: (id: string, body: UpdateManualSubscriptionBody) =>
    request('PATCH', `/api/admin/manual-subscriptions/${encodeURIComponent(id)}`, {
      body,
      schema: ManualSubscription,
    }),
  deleteManualSubscription: (id: string) =>
    request('DELETE', `/api/admin/manual-subscriptions/${encodeURIComponent(id)}`),

  importSheet: (
    kind: ImportKind,
    source: ImportSource,
    options: { dryRun: boolean; overwriteObjectives?: boolean },
  ) => {
    const path = importPath(kind, options);
    if ('file' in source) {
      const form = new FormData();
      form.append('file', source.file, source.file.name);
      return uploadForm(path, form, ImportResult);
    }
    return request('POST', path, { body: { spreadsheetUrl: source.spreadsheetUrl }, schema: ImportResult });
  },

  simulateSignature: (body: SimulateSignatureBody = {}) =>
    request('POST', '/api/dev/simulate-signature', { body, schema: QuoteSignedEvent }),

  /** Projets (lot D1a). */
  projects: () => request('GET', '/api/projects', { schema: ProjectList }),
  /** Lot BR : Suivi, Rendez-vous et « À regarder » (tous les projets, terminés compris). */
  projectBoard: () => request('GET', '/api/projects/board', { schema: ProjectBoard }),
  setProjectColumn: (id: string, column: BoardColumn) =>
    request('PUT', `/api/projects/${encodeURIComponent(id)}/column`, {
      body: { column },
      schema: ProjectBoardChange,
    }),
  linkProjectClient: (id: string, clientId: string) =>
    request('PUT', `/api/projects/${encodeURIComponent(id)}/client`, {
      body: { clientId },
      schema: ProjectBoardChange,
    }),
  undoProjectChange: (id: string, undoId: string) =>
    request('POST', `/api/projects/${encodeURIComponent(id)}/undo`, {
      body: { undoId },
      schema: ProjectBoardItem,
    }),
  project: (id: string) =>
    request('GET', `/api/projects/${encodeURIComponent(id)}`, { schema: ProjectDetail }),
  projectLoad: (range: { from?: string; to?: string } = {}) => {
    const query = new URLSearchParams(
      Object.entries(range).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
    ).toString();
    return request('GET', `/api/projects/load${query ? `?${query}` : ''}`, { schema: ProjectLoad });
  },
  /** Qui fait l'étape et / ou qui prévenir une fois cochée (lot M), sur ce projet seulement. */
  updateProjectStep: (
    projectId: string,
    stepId: string,
    patch: { whoUserId?: string | null; notifyUserIds?: string[] },
  ) =>
    request('PATCH', `/api/projects/${encodeURIComponent(projectId)}/steps/${encodeURIComponent(stepId)}`, {
      body: patch,
      schema: ProjectStep,
    }),
  /** Lot AR : ordre du planning (rangé à la main ou par date), ranger les projets visibles, revenir à la date. */
  // Lot BQ : événements de l'agenda qui ressemblent à un rendez-vous, à confirmer (plan `meeting.confirm`).
  meetingProposals: (projectId?: string) =>
    request(
      'GET',
      `/api/projects/meeting-proposals${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`,
      { schema: MeetingProposalList },
    ),
  dismissMeetingProposal: (refs: { projectId: string; meeting: string; eventId: string }) =>
    request(
      'POST',
      `/api/projects/${encodeURIComponent(refs.projectId)}/meetings/${encodeURIComponent(refs.meeting)}/dismiss`,
      { body: { eventId: refs.eventId } },
    ),
  restoreMeetingProposal: (refs: { projectId: string; meeting: string; eventId: string }) =>
    request(
      'DELETE',
      `/api/projects/${encodeURIComponent(refs.projectId)}/meetings/${encodeURIComponent(refs.meeting)}/dismiss`,
      { body: { eventId: refs.eventId } },
    ),
  projectOrder: () => request('GET', '/api/projects/order', { schema: ProjectOrderState }),
  saveProjectOrder: (ids: string[]) =>
    request('PUT', '/api/projects/order', { body: { ids }, schema: ProjectOrderState }),
  resetProjectOrder: () => request('DELETE', '/api/projects/order', { schema: ProjectOrderState }),
  /** Lot AR : renommer le projet dans le Cockpit (`name` seulement). */
  renameProject: (id: string, name: string) =>
    request('PATCH', `/api/projects/${encodeURIComponent(id)}`, { body: { name }, schema: ProjectDetail }),
  simulateProject: (body: SimulateProjectBody) =>
    request('POST', '/api/projects/simulate', { body, schema: SimulateProjectResult }),
  agendaWeek: (start?: string) =>
    request('GET', `/api/agenda/week${start ? `?start=${encodeURIComponent(start)}` : ''}`, {
      schema: AgendaWeek,
    }),
  /** Lot AF : rendez-vous R1 à R5 à venir de tous les agendas (compteurs de l'Agenda). */
  agendaUpcoming: () => request('GET', '/api/agenda/upcoming', { schema: AgendaUpcoming }),
  /** Lot AO : volet d'un R1 (infos, membres présents, fiche). */
  r1Detail: (r1Key: string) => request('GET', `/api/r1/${encodeURIComponent(r1Key)}`, { schema: R1Detail }),
  /** Lot AO : infos avant le R1 (champs ou « Rien à ajouter »). */
  putR1Prep: (r1Key: string, body: R1PrepInput) =>
    request('PUT', `/api/r1/${encodeURIComponent(r1Key)}/prep`, { body, schema: R1PrepResult }),
  /** Lot AX : relier le R1 à un client Axonaut (ou le changer), retirer le lien manuel. */
  linkR1Client: (r1Key: string, body: R1ClientBody) =>
    request('PUT', `/api/r1/${encodeURIComponent(r1Key)}/client`, { body, schema: R1ClientResult }),
  unlinkR1Client: (r1Key: string) =>
    request('DELETE', `/api/r1/${encodeURIComponent(r1Key)}/client`, { schema: R1ClientResult }),
  /** Lot AN (route du lot AN) : préparer ou régénérer la fiche maintenant, mail coché ou non. */
  r1Brief: (r1Key: string, body: R1BriefRequest) =>
    request('POST', `/api/r1/${encodeURIComponent(r1Key)}/brief`, { body, schema: R1Brief }),
  /** Lot AT : brouillon de devis après le R1 (le devis Axonaut passe par le plan `quote.create`). */
  quoteCatalog: () => request('GET', '/api/quote-drafts/catalog', { schema: QuoteCatalog }),
  quoteDrafts: (query: { r1Key?: string; status?: 'open' | 'all' } = {}) => {
    const params = new URLSearchParams();
    if (query.r1Key) params.set('r1Key', query.r1Key);
    if (query.status) params.set('status', query.status);
    const qs = params.toString();
    return request('GET', `/api/quote-drafts${qs ? `?${qs}` : ''}`, { schema: QuoteDraftSummaryList });
  },
  openQuoteDraft: (body: OpenQuoteDraftBody) =>
    request('POST', '/api/quote-drafts', { body, schema: QuoteDraft }),
  quoteDraft: (id: string) =>
    request('GET', `/api/quote-drafts/${encodeURIComponent(id)}`, { schema: QuoteDraft }),
  updateQuoteDraft: (id: string, body: UpdateQuoteDraftBody) =>
    request('PATCH', `/api/quote-drafts/${encodeURIComponent(id)}`, { body, schema: QuoteDraft }),
  proposeQuote: (id: string) =>
    request('POST', `/api/quote-drafts/${encodeURIComponent(id)}/propose`, { schema: QuoteDraft }),
  searchQuoteClient: (id: string) =>
    request('POST', `/api/quote-drafts/${encodeURIComponent(id)}/client-search`, {
      schema: QuoteClientSearchResult,
    }),
  dismissQuoteDraft: (id: string) =>
    request('POST', `/api/quote-drafts/${encodeURIComponent(id)}/dismiss`, { schema: QuoteDraft }),
  reopenQuoteDraft: (id: string) =>
    request('POST', `/api/quote-drafts/${encodeURIComponent(id)}/reopen`, { schema: QuoteDraft }),
  getSetting: (key: SettingKey) => request('GET', `/api/admin/settings/${key}`, { schema: SettingResponse }),
  putSetting: (key: SettingKey, value: unknown) =>
    request('PUT', `/api/admin/settings/${key}`, { body: { value }, schema: SettingResponse }),

  /** À faire (lot C1, écran du lot C2). */
  actions: (query: ActionsQuery = {}) => {
    const params = new URLSearchParams(
      Object.entries(query).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
    ).toString();
    return request('GET', `/api/actions${params ? `?${params}` : ''}`, { schema: z.array(Action) });
  },
  createAction: (body: CreateActionBody) => request('POST', '/api/actions', { body, schema: Action }),
  updateAction: (id: string, body: UpdateActionBody) =>
    request('PATCH', `/api/actions/${encodeURIComponent(id)}`, { body, schema: Action }),
  /** Lot AZ « Le prévenir » : canaux utilisables et dernier canal choisi ; message à la personne de la carte. */
  taskNotifyOptions: () => request('GET', '/api/me/task-notify', { schema: TaskNotifyOptions }),
  notifyAction: (id: string, body: TaskNotifyBody) =>
    request('POST', `/api/actions/${encodeURIComponent(id)}/notify`, { body, schema: TaskNotifyResult }),
  /** Lot AS : étapes d'une carte « Tâche » (une à la fois) et clients du sélecteur. */
  addChecklistItem: (id: string, body: AddChecklistItemBody) =>
    request('POST', `/api/actions/${encodeURIComponent(id)}/checklist`, { body, schema: Action }),
  updateChecklistItem: (id: string, itemId: string, body: UpdateChecklistItemBody) =>
    request('PATCH', `/api/actions/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, {
      body,
      schema: Action,
    }),
  deleteChecklistItem: (id: string, itemId: string) =>
    request('DELETE', `/api/actions/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, {
      schema: Action,
    }),
  clientOptions: () => request('GET', '/api/clients/options', { schema: ClientOptionList }),
  /** Lot AS (second temps) : images d'une carte « Tâche » (une image par envoi, 5 au plus). */
  uploadActionAttachment: (id: string, file: Blob) =>
    uploadFile(`/api/actions/${encodeURIComponent(id)}/attachments`, file, Action),
  deleteActionAttachment: (id: string, attachmentId: string) =>
    request(
      'DELETE',
      `/api/actions/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
      {
        schema: Action,
      },
    ),
  /** Lot X : « Pas de R2 pour ce client » sur « Fixer le R2 ». */
  skipR2: (id: string) =>
    request('POST', `/api/actions/${encodeURIComponent(id)}/skip-r2`, { schema: SkipR2Result }),
  /** Lot AX : « Devis déjà fait » sur « Préparer le devis » (client et devis choisis). */
  quoteDone: (id: string, body: QuoteDoneBody) =>
    request('POST', `/api/actions/${encodeURIComponent(id)}/quote-done`, { body, schema: QuoteDoneResult }),
  /** Lot AI [30] : « Pas d'abonnement à mettre » sur « Mettre en place l'abonnement ». */
  noSubscription: (id: string, body: NoSubscriptionBody) =>
    request('POST', `/api/actions/${encodeURIComponent(id)}/no-subscription`, {
      body,
      schema: NoSubscriptionResult,
    }),
  noSubscriptionClients: () =>
    request('GET', '/api/clients/no-subscription', { schema: ClientNoSubscriptionList }),
  clearNoSubscription: (clientId: string) =>
    request('DELETE', `/api/clients/${encodeURIComponent(clientId)}/no-subscription`, {
      schema: ClearNoSubscriptionResult,
    }),
  restoreNoSubscription: (clientId: string, undoId: string) =>
    request('POST', `/api/clients/${encodeURIComponent(clientId)}/no-subscription/restore`, {
      body: { undoId },
    }),
  /** Lot AK : responsables des relances de devis des clients ; « Retirer ». */
  quoteFollowupOwners: () =>
    request('GET', '/api/clients/quote-followup-owners', { schema: ClientQuoteFollowupOwnerList }),
  clearQuoteFollowupOwner: (clientId: string) =>
    request('DELETE', `/api/clients/${encodeURIComponent(clientId)}/quote-followup-owner`, {
      schema: ClearQuoteFollowupOwnerResult,
    }),
  restoreQuoteFollowupOwner: (clientId: string, undoId: string) =>
    request('POST', `/api/clients/${encodeURIComponent(clientId)}/quote-followup-owner/restore`, {
      body: { undoId },
    }),
  doneActions: (since?: string) =>
    request('GET', `/api/actions/done${since ? `?since=${encodeURIComponent(since)}` : ''}`, {
      schema: z.array(Action),
    }),
  actionStakes: (who: string = 'all') =>
    request('GET', `/api/actions/stakes?who=${encodeURIComponent(who)}`, { schema: ActionStakes }),
  draftEmail: (body: DraftEmailBody) => request('POST', '/api/drafts/email', { body, schema: DraftEmail }),

  /** Carte (lot E1). */
  mapClients: () => request('GET', '/api/map/clients', { schema: MapClientsResponse }),
  geocoding: () => request('GET', '/api/admin/geocoding', { schema: GeocodingOverview }),
  runGeocoding: () => request('POST', '/api/admin/geocoding/run', { schema: GeocodingRunQueued }),
};
